import {
  Clickable,
  Collider,
  ColliderShape,
  type EngineContext,
  ensureTextTexture,
  FollowPointer,
  PlaySoundOnClick,
  PulseScale,
  Scene,
  SpriteRenderer,
  Vector2,
} from '@kite/engine';
import { CollectOnTouch } from './behaviors/CollectOnTouch';
import { Wander } from './behaviors/Wander';
import {
  ASSET_MANIFEST,
  BACKGROUND_COLOR,
  BACKGROUND_SIZE,
  BEE_KEY,
  BEE_SIZE,
  BEE_SPEED,
  BEE_TURN_SECONDS,
  CAPTION_COLOR,
  CAPTION_FONT_SIZE,
  CAPTION_TEXT,
  CAPTION_Y,
  CAR_COLLIDER_RADIUS,
  CAR_GROUP,
  CAR_KEY,
  CAR_SIZE,
  CAR_SPEED,
  COIN_COLLIDER_RADIUS,
  COIN_GROUP,
  COIN_KEY,
  COIN_POSITIONS,
  COIN_PULSE_CYCLES_PER_SECOND,
  COIN_PULSE_PHASE_STEP_SECONDS,
  COIN_RESPAWN_CLEARANCE,
  COIN_SIZE,
  COIN_SPARKLES,
  COLLECT_SOUND_KEY,
  FIELD_COLOR,
  FIELD_CORNER_INSET,
  HORN_SOUND_KEY,
  PLAY_AREA,
  SortOrder,
} from './content';

/**
 * Builds a fresh field of coins, a car that chases the finger and a bee that does not.
 *
 * The whole game is engine parts wired together with content: `FollowPointer` drives the car,
 * `CollisionSystem` notices a coin being touched, `PulseScale` makes the coins look tappable and
 * `PlaySoundOnClick` answers a tap on the car. Only two rules are the game's own, and both are
 * behaviors in this package: what a collected coin does, and how the bee drifts.
 *
 * Called afresh every launch, so nothing here may be shared between scenes.
 */
export function createChaseCarScene(context: EngineContext): Scene {
  const scene = new Scene(context, { name: 'Chase the Car' });

  startLoadingAssets(context);
  createBackdrop(scene);
  createCaption(scene, context);
  createCoins(scene);
  createBee(scene);
  createCar(scene);

  return scene;
}

/*
 * Loading is started and never awaited. Scene building is synchronous by design and the render system
 * resolves texture keys at the last possible moment, so a textured sprite draws as a plain white quad
 * of the right size until its art lands and swaps itself in, with no loading screen and nothing to
 * notify. Textured sprites keep the default white tint, because the sprite color multiplies into the
 * texture: any other value would repaint the art rather than stand in for it.
 */
function startLoadingAssets(context: EngineContext): void {
  void context.assets.load(ASSET_MANIFEST).catch(() => undefined);
}

/*
 * Colors and sizes are cloned out of the content module on the way in. A SpriteRenderer keeps the
 * instance it is handed, so passing a module constant straight through would let one launch's grass
 * share a Color with every launch that follows it.
 */
function createBackdrop(scene: Scene): void {
  const grass = scene.createEntity('Grass');
  grass.addComponent(
    new SpriteRenderer({
      color: BACKGROUND_COLOR.clone(),
      size: BACKGROUND_SIZE.clone(),
      sortOrder: SortOrder.Background,
    }),
  );

  const field = scene.createEntity('Field');
  field.transform.position.set(PLAY_AREA.centerX, PLAY_AREA.centerY);
  field.addComponent(
    new SpriteRenderer({
      color: FIELD_COLOR.clone(),
      size: new Vector2(PLAY_AREA.width + FIELD_CORNER_INSET, PLAY_AREA.height + FIELD_CORNER_INSET),
      sortOrder: SortOrder.Field,
    }),
  );
}

function createCaption(scene: Scene, context: EngineContext): void {
  const label = ensureTextTexture(context.assets, CAPTION_TEXT, {
    fontSize: CAPTION_FONT_SIZE,
    color: CAPTION_COLOR,
  });

  const caption = scene.createEntity('Caption');
  caption.transform.position.set(0, CAPTION_Y);
  caption.addComponent(
    new SpriteRenderer({
      texture: label.key,
      size: new Vector2(label.width, label.height),
      sortOrder: SortOrder.Caption,
    }),
  );
}

function createCoins(scene: Scene): void {
  for (const [index, position] of COIN_POSITIONS.entries()) {
    const coin = scene.createEntity(`Coin ${String(index + 1)}`);
    coin.transform.position.copyFrom(position);
    coin.addTag('collectible');

    coin.addComponent(new SpriteRenderer({ texture: COIN_KEY, size: COIN_SIZE.clone(), sortOrder: SortOrder.Coin }));
    coin.addComponent(
      new Collider({ radius: COIN_COLLIDER_RADIUS, group: COIN_GROUP, mask: CAR_GROUP, shape: ColliderShape.Circle }),
    );

    coin.addBehavior(
      new PulseScale({
        cyclesPerSecond: COIN_PULSE_CYCLES_PER_SECOND,
        phaseSeconds: index * COIN_PULSE_PHASE_STEP_SECONDS,
      }),
    );
    coin.addBehavior(
      new CollectOnTouch({
        soundKey: COLLECT_SOUND_KEY,
        area: PLAY_AREA,
        clearance: COIN_RESPAWN_CLEARANCE,
        sparkles: COIN_SPARKLES,
      }),
    );
  }
}

function createBee(scene: Scene): void {
  const bee = scene.createEntity('Bee');
  bee.transform.position.set(PLAY_AREA.left, PLAY_AREA.top);
  bee.addComponent(new SpriteRenderer({ texture: BEE_KEY, size: BEE_SIZE.clone(), sortOrder: SortOrder.Bee }));
  bee.addBehavior(new Wander({ area: PLAY_AREA, speed: BEE_SPEED, turnSeconds: BEE_TURN_SECONDS }));
}

function createCar(scene: Scene): void {
  const car = scene.createEntity('Car');
  car.addComponent(new SpriteRenderer({ texture: CAR_KEY, size: CAR_SIZE.clone(), sortOrder: SortOrder.Car }));
  car.addComponent(new Collider({ radius: CAR_COLLIDER_RADIUS, group: CAR_GROUP, mask: COIN_GROUP }));
  car.addComponent(new Clickable());
  car.addBehavior(new FollowPointer({ speed: CAR_SPEED, faceDirection: true }));
  car.addBehavior(new PlaySoundOnClick(HORN_SOUND_KEY));
}
